import { prefersReducedMotion } from '@theme/utilities';

class ParallaxHero extends HTMLElement {
  /** @type {number | null} */
  #measureRafId = null;

  /** @type {HTMLElement | null} */
  #media = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #sectionHeight = 0;

  /** @type {number} */
  #sectionLeft = 0;

  /** @type {number} */
  #sectionWidth = 0;

  /** @type {'before' | 'fixed' | 'after' | null} */
  #lastState = null;

  /** @type {number | null} */
  #lastClipBottom = null;

  connectedCallback() {
    this.#media = this.querySelector('[data-parallax-media]');
    if (!this.#media) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    window.addEventListener('scroll', this.#queueUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#queueMeasure, { passive: true, signal });
    window.addEventListener('load', this.#queueMeasure, { once: true, signal });

    this.#resizeObserver = new ResizeObserver(this.#queueMeasure);
    this.#resizeObserver.observe(this);

    this.#measure();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#resizeObserver?.disconnect();

    if (this.#measureRafId !== null) {
      cancelAnimationFrame(this.#measureRafId);
      this.#measureRafId = null;
    }
  }

  #queueUpdate = () => {
    // Update synchronously in the scroll handler (not via rAF) so the clip is
    // recomputed in the same frame the browser paints the new scroll position.
    // Deferring to the next frame leaves the fixed media painted with a stale
    // clip for one frame — a brief white sliver when scrolling.
    this.#update();
  };

  #queueMeasure = () => {
    if (this.#measureRafId !== null) return;

    this.#measureRafId = requestAnimationFrame(this.#measure);
  };

  #measure = () => {
    this.#measureRafId = null;

    const rect = this.getBoundingClientRect();

    this.#sectionHeight = rect.height;
    this.#sectionLeft = rect.left;
    this.#sectionWidth = rect.width;
    this.#lastState = null;
    this.#lastClipBottom = null;
    this.#update();
  };

  #update = () => {
    if (!this.#media || prefersReducedMotion()) {
      this.#setRestingState('before');
      return;
    }

    // Read the section's live viewport position every frame instead of caching
    // it at measure time. Content above the section (lazy-loaded media) can
    // shift it down after the last measure, and scroll events don't re-measure
    // — a stale top over-clips the media and leaves a white gap below it.
    const rectTop = this.getBoundingClientRect().top;

    if (rectTop > 0) {
      this.#setRestingState('before');
      return;
    }

    if (rectTop <= -this.#sectionHeight) {
      this.#setRestingState('after');
      return;
    }

    this.#setFixedState(-rectTop);
  };

  /**
   * @param {'before' | 'after'} state
   */
  #setRestingState(state) {
    if (!this.#media || this.#lastState === state) return;

    this.#lastState = state;
    this.#lastClipBottom = null;
    this.#media.style.removeProperty('position');
    this.#media.style.removeProperty('top');
    this.#media.style.removeProperty('right');
    this.#media.style.removeProperty('bottom');
    this.#media.style.removeProperty('left');
    this.#media.style.removeProperty('width');
    this.#media.style.removeProperty('height');
    this.#media.style.removeProperty('clip-path');
    this.#media.style.removeProperty('transform');
  }

  /**
   * @param {number} scrolledPastTop
   */
  #setFixedState(scrolledPastTop) {
    if (!this.#media) return;

    if (this.#lastState !== 'fixed') {
      this.#lastState = 'fixed';
      this.#media.style.position = 'fixed';
      this.#media.style.top = '0';
      this.#media.style.right = 'auto';
      this.#media.style.bottom = 'auto';
      this.#media.style.left = `${this.#sectionLeft}px`;
      this.#media.style.width = `${this.#sectionWidth}px`;
      this.#media.style.height = `${this.#sectionHeight}px`;
      this.#media.style.transform = 'translate3d(0, 0, 0)';
      this.#lastClipBottom = null;
    }

    const clipBottom = Math.min(Math.max(scrolledPastTop, 0), this.#sectionHeight);
    if (this.#lastClipBottom === clipBottom) return;

    this.#lastClipBottom = clipBottom;
    this.#media.style.clipPath = `inset(0 0 ${clipBottom}px 0)`;
  }
}

if (!customElements.get('parallax-hero')) {
  customElements.define('parallax-hero', ParallaxHero);
}
